/**
 * Tier & Add-ons accordion section
 *
 * Three-column TierTile grid (selectable; "Current" chip stays anchored to the
 * member's currently-paid tier even when a different tile is picked), followed
 * by an AddOnRow list filtered to the selected tier's compatible add-ons, and
 * an inline cost-impact line that appears once the user changes either. Uses
 * the compact TierTile / AddOnRow variants because the modal real estate
 * doesn't fit the marketing-style TierCard / AddOnCard used on pricing pages.
 */

import { useMemo } from 'react';
import {
  AddOnRow,
  type BundleWithStatus,
  formatPrice,
  getAddOnsIncludedInTier,
  TierTile,
} from '@features/subscriptions';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Collapse,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

interface CostInfo {
  cents: number;
  currency: string;
}

interface TierAddOnsSectionProps {
  isClinician: boolean;
  canSelectBundles: boolean;
  isCatalogLoading: boolean;
  tiers: BundleWithStatus[];
  availableAddOns: BundleWithStatus[];
  selectedTierId: string;
  selectedAddOnIds: Set<string>;
  currentTierBundleId: string | undefined;
  onTierChange: (tierId: string) => void;
  onToggleAddOn: (addOnId: string) => void;
  isTrialing: boolean;
  hasPlanChanges: boolean;
  currentCost: CostInfo;
  projectedCost: CostInfo;
  billingInterval: 'month' | 'year';
}

function CostImpactLine({
  current,
  projected,
  isTrialing,
}: {
  current: CostInfo;
  projected: CostInfo;
  isTrialing: boolean;
}) {
  const delta = projected.cents - current.cents;
  const deltaColor = delta > 0 ? 'warning.main' : 'success.main';
  const formatDelta = () => {
    if (delta === 0) return null;
    const absStr = formatPrice(Math.abs(delta), projected.currency);
    return delta > 0 ? `+${absStr}/mo` : `saves ${absStr}/mo`;
  };

  return (
    <Box
      sx={{
        py: 1,
        px: 1.5,
        borderRadius: 1,
        backgroundColor: theme => alpha(theme.palette.primary.main, 0.06),
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {isTrialing ? 'After trial: ' : ''}
        <Typography component="span" variant="body2" sx={{ textDecoration: 'line-through' }}>
          {formatPrice(current.cents, current.currency)}/mo
        </Typography>{' '}
        →{' '}
        <Typography
          component="span"
          variant="body2"
          sx={{ fontWeight: 700, color: 'primary.main' }}
        >
          {formatPrice(projected.cents, projected.currency)}/mo
        </Typography>{' '}
        {delta !== 0 && (
          <Typography
            component="span"
            variant="caption"
            sx={{ color: deltaColor, fontWeight: 600 }}
          >
            ({formatDelta()})
          </Typography>
        )}
      </Typography>
    </Box>
  );
}

export function TierAddOnsSection({
  isClinician,
  canSelectBundles,
  isCatalogLoading,
  tiers,
  availableAddOns,
  selectedTierId,
  selectedAddOnIds,
  currentTierBundleId,
  onTierChange,
  onToggleAddOn,
  isTrialing,
  hasPlanChanges,
  currentCost,
  projectedCost,
  billingInterval,
}: TierAddOnsSectionProps) {
  // Set of add-on bundle_ids fully covered by the currently-selected tier's
  // grants — these render as locked "Included" rows instead of priced toggles.
  // Computed before any early returns so the hook order stays stable across
  // role/loading transitions.
  const selectedTier = tiers.find(t => t.bundle_id === selectedTierId);
  const includedAddOnIds = useMemo(() => {
    if (!selectedTier) return new Set<string>();
    return new Set(
      getAddOnsIncludedInTier(selectedTier, availableAddOns).map(a => a.bundle_id),
    );
  }, [selectedTier, availableAddOns]);

  // Non-clinicians: section grayed out per spec. Info alert explains why.
  if (!isClinician) {
    return (
      <Alert
        severity="info"
        icon={<InfoOutlinedIcon fontSize="small" />}
        sx={{ fontSize: '0.85rem' }}
      >
        Tier and add-ons apply to clinicians only. Add the Clinician role to enable plan selection.
      </Alert>
    );
  }

  // Bundling globally off — nothing to show.
  if (!canSelectBundles) {
    return (
      <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
        Plan management is not enabled for this organization.
      </Alert>
    );
  }

  if (isCatalogLoading) {
    return (
      <Grid container spacing={2}>
        {[0, 1, 2].map(i => (
          <Grid size={{ xs: 12, md: 4 }} key={i}>
            <Skeleton variant="rounded" sx={{ height: 80 }} />
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        {tiers.map(tier => (
          <Grid size={{ xs: 12, md: 4 }} key={tier.bundle_id}>
            <TierTile
              bundle={tier}
              billingInterval={billingInterval}
              isSelected={tier.bundle_id === selectedTierId}
              isCurrent={tier.bundle_id === currentTierBundleId}
              onSelect={onTierChange}
            />
          </Grid>
        ))}
      </Grid>

      {selectedTierId && availableAddOns.length > 0 && (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Add-ons
          </Typography>
          <Stack spacing={1}>
            {availableAddOns.map(addOn => (
              <AddOnRow
                key={addOn.bundle_id}
                bundle={addOn}
                billingInterval={billingInterval}
                isSelected={selectedAddOnIds.has(addOn.bundle_id)}
                isIncluded={includedAddOnIds.has(addOn.bundle_id)}
                onToggle={onToggleAddOn}
              />
            ))}
          </Stack>
        </Box>
      )}

      <Collapse in={hasPlanChanges}>
        <CostImpactLine
          current={currentCost}
          projected={projectedCost}
          isTrialing={isTrialing}
        />
      </Collapse>
    </Stack>
  );
}
